'use client';
import { Autocomplete, autocompleteClasses, Box, useBreakpoints, useNiceModal } from '@castlery/fortress';
import {
  CouponItemV1,
  getCouponsByOrderNumberV1,
  getYotpoCustomerDetails,
  getYotpoRedemptionOptions,
  selectApplyCouponLoading,
} from '@castlery/modules-order-domain';
import { redeemYotpoCreditsCommand } from '@castlery/modules-order-services';
import { trackOfflineCoupon } from '@castlery/modules-tracking-services';
import { selectedCurrentCustomer } from '@castlery/modules-user-domain';
import { makePersistenceHandles } from '@castlery/shared-persistence-kit';
import { useAppDispatch, useAppSelector } from '@castlery/shared-redux-store';
import { applyCouponCommand } from '@castlery/shared-services';
import React, { useEffect, useMemo, useState } from 'react';
import { useMount } from 'react-use';
import { CouponApplyButton } from '../coupon-apply-button/coupon-apply-button';
import { CouponCancelButton } from '../coupon-cancel-button/coupon-cancel-button';
import { renderOption } from './coupon-dropdown-option';
import { checkEligible, formatter, formatterRedeemOptions, type CouponItemType } from './helper';
import { groupBy, renderGroup } from './render-group';

export interface CouponDropdownProps {
  editToggle: () => void;
  coupons: CouponItemV1[];
}

export const CouponDropdown = ({ editToggle }: CouponDropdownProps) => {
  const { mobile } = useBreakpoints();
  const [modal, contextHolder] = useNiceModal();

  const orderNumber = makePersistenceHandles().orderNumber.getItem() as string;
  const customer = useAppSelector(selectedCurrentCustomer) as { email: string; id: number } | null;
  const getCoupons = useMemo(() => getCouponsByOrderNumberV1.select(orderNumber), [orderNumber]);
  const getPoints = useMemo(() => getYotpoCustomerDetails.select(customer?.email || ''), [customer]);
  const getRedeemOptions = useMemo(
    () => getYotpoRedemptionOptions.select({ customerEmail: customer?.email || '', customerId: customer?.id || 0 }),
    [customer]
  );
  const { data: coupons = [] } = useAppSelector(getCoupons);
  const { data: yotpoCustomerDetails = {} } = useAppSelector(getPoints);
  const points = yotpoCustomerDetails?.points_balance || 0;

  const { data: redeemOptions = [] } = useAppSelector(getRedeemOptions);
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectApplyCouponLoading);
  const [couponCode, setCouponCode] = React.useState<string>('');
  const [open, setOpen] = useState(false);

  const newCoupons = formatter(coupons);
  const newRedeemOptions = formatterRedeemOptions(redeemOptions, points);
  const mergeList = [...newCoupons, ...newRedeemOptions];
  const value = newCoupons.find((coupon) => coupon.code === couponCode);

  const apply = async (code: string) => {
    await dispatch(trackOfflineCoupon('select'));
    await dispatch(applyCouponCommand({ couponCode: code }));
    editToggle && editToggle();
  };

  const redeem = async (id: number) => {
    await dispatch(trackOfflineCoupon('redeem'));
    modal.warning({
      title: 'This redemption cannot be undone.',
      subDesc: 'Once redeem, action cannot be reversed. Would you like to proceed?',
      confirmText: 'Confirm',
      onConfirm: async () => await dispatch(redeemYotpoCreditsCommand(id)),
      beforeClose: () => setOpen(true),
      // 禁止掉autoFocus，是因为focus从autocomplete转移到modal,会导致autocomplete失焦而触发onclose事件
      disableAutoFocus: true,
    });
  };

  const onSelect = async (event: React.SyntheticEvent, value: CouponItemType, reason: string) => {
    if (!value) return;
    if (value.couponType === 'coupon') {
      const { expired_at, available, code } = value;
      const eligible = checkEligible({ available, expired_at });
      if (eligible && code) {
        setCouponCode(code);
        setOpen(false);
        await apply(code);
      }
    }
    if (value.couponType === 'credits') {
      setOpen(false);
      await redeem(value.id);
    }
  };

  const onOpen = () => setOpen(true);
  const onClose = (event: React.SyntheticEvent, reason: string) => {
    if (['toggleInput'].includes(reason)) return;

    if (!loading) {
      setOpen(false);
      editToggle && editToggle();
    }
  };
  const onInputChange = (event: React.SyntheticEvent, newInputValue: string, reason: string) => {
    if (reason === 'input') {
      setCouponCode(newInputValue);
    }
  };
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && couponCode) {
      apply(couponCode);
    }
  };
  useMount(() => {
    if (mobile) {
      setOpen(true);
    }
  });

  useEffect(() => {
    function checkFocus() {
      // 因为mobile禁用掉了autoFocus，所以coupon dropdown 首次打开时，不受focus控制，
      // 点击其他地方弹出dialog时，focus不会blur掉，所以不会触发autocomplete的onClose事件，需要手动关闭autocomplete
      if (mobile && open && (document?.activeElement as HTMLInputElement)?.name !== 'couponInput') {
        setOpen(false);
      }
      return false;
    }

    window?.addEventListener('focus', checkFocus, true);
    return () => {
      window?.removeEventListener('focus', checkFocus, true);
    };
  }, [mobile, open]);

  return (
    <>
      <Autocomplete
        openOnFocus
        autoComplete
        autoFocus={!mobile}
        open={open}
        value={value}
        name="couponInput"
        inputValue={couponCode}
        sx={{
          paddingY: 0.65,
          flex: 1,
          border: 'none',
          boxShadow: 'none',
          [`& .${autocompleteClasses.popupIndicator}, & .${autocompleteClasses.clearIndicator}`]: {
            display: 'none',
          },
          [`& .${autocompleteClasses.input}`]: {
            sm: { maxWidth: 'auto' },
            md: { maxWidth: 110 },
            lg: { maxWidth: 'auto' },
          },
        }}
        endDecorator={
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
            <CouponApplyButton couponCode={couponCode} editToggle={editToggle} />
            <CouponCancelButton editToggle={editToggle} />
          </Box>
        }
        options={mergeList}
        getOptionLabel={(option) => {
          return option.code || option.id.toString();
        }}
        onChange={onSelect as any}
        onOpen={onOpen}
        onClose={onClose}
        onInputChange={onInputChange}
        onKeyDown={onKeyDown}
        renderOption={renderOption}
        groupBy={groupBy}
        renderGroup={(params) => renderGroup(params, points)}
        disableCloseOnSelect={true}
        // ------------------------------ note -------------------------------
        // 不能用getOptionDisabled，因为auto complete的disabled会导致被禁用option项中的tootip不能响应事件
        // disabledItemsFocusable
        // getOptionDisabled={}
        // ------------------------------- test ------------------------------
        // isOptionEqualToValue
      />
      {contextHolder}
    </>
  );
};
